/** Why a page part loads: `viewport` parts are in sight and load first. */
export type PagePartLoadReason = 'page-part-load' | 'viewport';
export type SystemDocLoadContext =
  | {
      kind: 'database-row-shard';
      workspaceId: string;
      blockId: string;
      databaseId: string;
      reason: string;
    }
  | {
      kind: 'page-part';
      workspaceId: string;
      pageId: string;
      partId: string;
      reason: PagePartLoadReason;
    };
export type SystemDocLoader = (
  docId: string,
  context: SystemDocLoadContext
) => void | (() => void);
const loadersByHost = new WeakMap<object, Set<SystemDocLoader>>();

export function registerSystemDocLoader(
  host: object,
  loader: SystemDocLoader
): () => void {
  let loaders = loadersByHost.get(host);
  if (!loaders) {
    loaders = new Set();
    loadersByHost.set(host, loaders);
  }
  loaders.add(loader);
  return () => {
    loaders.delete(loader);
    if (!loaders.size) loadersByHost.delete(host);
  };
}

export function requestSystemDocLoad(
  host: object & { workspace?: object },
  docId: string,
  context: SystemDocLoadContext
): (() => void) | undefined {
  const loaders =
    loadersByHost.get(host) ??
    (host.workspace ? loadersByHost.get(host.workspace) : undefined);
  if (!loaders?.size) return undefined;
  const disposers: (() => void)[] = [];
  try {
    for (const loader of loaders) {
      const dispose = loader(docId, context);
      if (dispose) disposers.push(dispose);
    }
  } catch (error) {
    for (const dispose of disposers) dispose();
    throw error;
  }
  let disposed = false;
  return () => {
    if (disposed) return;
    disposed = true;
    for (const dispose of disposers) dispose();
  };
}

export function ensureWorkspaceDoc<T>(
  workspace: {
    getDoc(id: string): T | null | undefined;
    registerDoc?(id: string): void;
    createDoc(id: string): T;
  },
  docId: string
): T | undefined {
  const existing = workspace.getDoc(docId);
  if (existing) return existing;
  if (workspace.registerDoc) {
    workspace.registerDoc(docId);
    return workspace.getDoc(docId) ?? undefined;
  }
  return workspace.createDoc(docId);
}
